import { Autocomplete, Box, Checkbox, FormControl, TextField, Tooltip, Chip, IconButton, ListSubheader, CircularProgress, Stack, Typography, Paper, useTheme } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import React, { useEffect, useState } from "react";
import { getNeonColor, scrollable } from "../../theme";
import { NeonChip } from "../../NeonComponents/NeonChip";

export function MultipleSelectorGrouped<Option>(props: {
    options: Option[];
    selected: Option[];
    updateSelection: (new_selection: Option[]) => void;
    getOptionLabel: (option: Option) => string;
    getOptionShort: (option: Option) => string;
    getGroup: (option: Option) => string;
    limitTags: number;
    label?: string | undefined;
    optionDisabled?: (option: Option) => boolean;
    optionDescription?: (option: Option) => string | undefined | null;
    optionDecoration?: (option: Option) => any;
}) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const [selectedGroups, setSelectedGroups] = useState([] as string[]);
    const [openedGroups, setOpenedGroups] = useState([] as string[]);
    const [userTyping, setUserTyping] = useState(false as boolean);
    const [selectedItems, setSelectedItems] = useState<Option[]>([]);
    const [inputValue, setInputValue] = useState(undefined as string|undefined);
    const [open, setOpen] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    
    // Update selectedItems when props.selected changes
    useEffect(() => {
        if (Array.isArray(props.selected)) {
            setSelectedItems(props.selected);
        }
    }, [props.selected]);
    
    const safeOptions = Array.isArray(props.options) ? props.options : [];
    
    if (safeOptions.length === 0) {
        return (
            <Stack
                direction={"row"}
                sx={{
                    alignItems: "center",
                    justifyContent: "space-evenly",
                    display: "flex",
                    height:'57px'
                }}
            >
                <CircularProgress />
                <Typography>{`Loading ${props.label}...`}</Typography>
            </Stack>
        );
    }

    const getGroupItems = (group: string) => safeOptions.filter((option: Option) => props.getGroup(option) === group);
    const unselectItem = (item: Option) => {
        const new_selection = selectedItems.filter((selectedItem) => item !== selectedItem);
        setSelectedItems(new_selection);
        if (props.selected !== new_selection) props.updateSelection(new_selection);
    };

    const isGroupFullySelected = (group: string) => {
        const group_items = getGroupItems(group);
        return group_items.every(item => selectedItems.includes(item));
    };

    const setGroupSelection = (event: any, new_group: string) => {
        const group_items = getGroupItems(new_group);
        if (selectedGroups.includes(new_group)) {
            const new_groups = selectedGroups.filter((group) => group !== new_group);
            setSelectedGroups(new_groups);

            const new_selected_items = selectedItems.filter((option) => !group_items.includes(option));
            setSelectedItems(new_selected_items);
        } else {
            const ng = [...selectedGroups, new_group];
            setSelectedGroups(ng);
            const new_selected_items = [...selectedItems, ...group_items.filter((option) => !selectedItems.includes(option))];
            setSelectedItems(new_selected_items);
        }
    };

    const setGroupDisplay = (event: any, group: string) => {
        if (!openedGroups.includes(group)) {
            setOpenedGroups([...openedGroups, group]);
        } else {
            setOpenedGroups(openedGroups.filter((value: string) => value !== group));
        }
    };

    const handleClick = (event: React.MouseEvent) => {
        // Don't open if clicking on a chip's delete button
        if ((event.target as HTMLElement).closest('.MuiChip-deleteIcon')) {
            return;
        }
        // Don't open if clicking on the input field (it will handle its own opening)
        if ((event.target as HTMLElement).tagName === 'INPUT') {
            return;
        }
        setOpen(true);
    };

    // Group selected items by their group
    const groupedSelectedItems = selectedItems.reduce((acc, item) => {
        const group = props.getGroup(item);
        if (!acc[group]) {
            acc[group] = [];
        }
        acc[group].push(item);
        return acc;
    }, {} as Record<string, Option[]>);

    const tooltipContent = (
        <Paper 
            elevation={8} 
            sx={{ 
                p: 1.5, 
                maxWidth: 350, 
                maxHeight: 400, 
                overflow: 'hidden',
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
                borderRadius: '12px',
                position: 'relative',
                boxShadow: `0px 0px 20px rgba(25, 25, 35, 0.95), 0px 0px 10px ${neonColor}40`,

            }}
        >
            <Stack direction={'column'} spacing={1} sx={{ position: 'relative', zIndex: 1 }}>
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 1, 
                        color: neonColor, 
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                    }}
                >
                    Selected {props.label || 'Items'} ({selectedItems.length})
                </Typography>
                {Object.entries(groupedSelectedItems).map(([group, items]) => (
                    <Stack direction={'column'} spacing={1} key={group}>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: neonColor, 
                                fontWeight: 'bold', 
                                fontSize: '0.75rem',
                            }}
                        >
                            {group} ({items.length})
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" alignItems="flex-start" sx={{ alignContent: 'flex-start', gap: '10px' }}>
                            {items.map((item, index) => (
                                <NeonChip
                                    key={index}
                                    label={props.getOptionShort(item)}
                                    onClick={() => unselectItem(item)}
                                    size="medium"
                                    variant="default"
                                />
                            ))}
                        </Stack>
                    </Stack>
                ))}
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: `${neonColor}CC`, 
                        fontStyle: 'italic',
                        mt: 1,
                        pt: 0.5,
                        display: 'block',
                        fontSize: '0.65rem'
                    }}
                >
                    💡 Click to modify selection
                </Typography>
            </Stack>
        </Paper>
    );

    return (
        <Tooltip
            title={tooltipContent}
            open={showTooltip && selectedItems.length > 0 && !open}
            onOpen={() => setShowTooltip(true)}
            onClose={() => setShowTooltip(false)}
            placement="bottom-start"
            arrow
            PopperProps={{
                sx: {
                    '& .MuiTooltip-tooltip': {
                        backgroundColor: 'transparent',
                        p: 0,
                        maxWidth: 'none'
                    },
                    '& .MuiTooltip-arrow': {
                        color: theme.palette.background.paper
                    }
                }
            }}
        >
                <Box 
                    onClick={handleClick} 
                    onMouseEnter={() => {
                        setShowTooltip(true);
                    }}
                    onMouseLeave={() => {
                        setShowTooltip(false);
                    }}
                    sx={{ cursor: 'pointer', position: 'relative' }}
                >
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => {
                        setOpen(false);
                        setUserTyping(false);
                        props.updateSelection(selectedItems);
                    }}
                    // disableClearable
                    inputValue={inputValue}
                    sx={{
                        height: '56px', 
                        maxHeight:'56px',
                        minHeight:'56px',
                        '& .MuiAutocomplete-inputRoot': {
                            height: '56px',
                            maxHeight: '56px',
                            minHeight: '56px',
                            flexWrap: 'nowrap',
                            overflow: 'hidden'
                        },
                        '& .MuiAutocomplete-tag': {
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        },
                        '& .MuiChip-deleteIcon': {
                            cursor: 'pointer'
                        },
                        '& .MuiInputLabel-root': {
                            color: 'text.secondary',
                            transition: 'color 0.15s ease-in-out, transform 0.2s ease-out'
                        },
                        '&:hover .MuiInputLabel-root': {
                            color: neonColor,
                            zIndex: 3
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: neonColor,
                            transition: 'color 0.15s ease-in-out, transform 0.2s ease-out',
                            zIndex: 3
                        },
                        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                            backgroundColor: 'background.paper',
                            px: 0.5
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.23)'
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.23)'
                        },
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 0
                        }
                    }}
                    options={safeOptions}
                    getOptionLabel={props.getOptionLabel}
                    value={selectedItems}
                    groupBy={props.getGroup}
                    renderGroup={(params) => {
                        const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
                        const checkedIcon = <CheckBoxIcon fontSize="small" />;
                        return (
                            <Box key={params.group}>
                                <ListSubheader 
                                    sx={{ 
                                        cursor: "pointer",
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        gap: 0.1,
                                        overflow: 'hidden',
                                        minWidth: 0
                                    }}
                                    onClick={(event) => setGroupDisplay(event, params.group)}
                                >
                                    <Checkbox 
                                        icon={icon} 
                                        checkedIcon={checkedIcon} 
                                        checked={isGroupFullySelected(params.group)} 
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setGroupSelection(event, params.group);
                                        }} 
                                    />
                                    <Typography 
                                        variant="subtitle1"
                                        sx={{ 
                                            flex: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                            color: neonColor,
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {params.group}
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: 'text.secondary',
                                            fontSize: '0.75rem',
                                            width: '55px',
                                            flexShrink: 0,
                                            textAlign: 'left',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        ({selectedItems.filter(item => props.getGroup(item) === params.group).length} / {getGroupItems(params.group).length})
                                    </Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setGroupDisplay(event, params.group);
                                        }}
                                    >
                                        {openedGroups.includes(params.group) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </ListSubheader>
                                {openedGroups.includes(params.group) || userTyping ? params.children : <></>}
                            </Box>
                        );
                    }}

                    onChange={(event: any, new_value) => {
                        // setUserTyping(false);
                        setSelectedItems(new_value);
                        setInputValue(inputValue);
                    }}
                    renderInput={(params) => <TextField {...params} label={props.label} variant="outlined" />}
                    renderOption={(renderOptionProps, option: Option, { selected }) => {
                        const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
                        const checkedIcon = <CheckBoxIcon fontSize="small" />;
                        return (
                            <Tooltip title={props.optionDescription ? props.optionDescription(option) : undefined} placement="left">
                                <li {...renderOptionProps}>
                                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                    {props.optionDecoration ? props.optionDecoration(option) : <></>}
                                    {props.getOptionLabel(option)}
                                </li>
                            </Tooltip>
                        );
                    }}

                    onInputChange={(event, newValue, reason) => {
                        if(reason === 'reset') return;
                        setInputValue(newValue)
                        setUserTyping(true)
                    }}
                    getOptionDisabled={(option) => (props.optionDisabled ? props.optionDisabled(option) : false)}
                    renderTags={(value, getTagProps) => {
                        const numTags = Array.isArray(value) ? value.length : 0;
                        const safeValue = Array.isArray(value) ? value : [];
                        return (
                            <Stack 
                                direction={"row"}
                                spacing={0.5}
                                sx={{ 
                                    alignItems: "center",
                                    flexWrap: 'nowrap',
                                    overflow: 'hidden',
                                    width: '100%'
                                }}
                            >
                                {safeValue.slice(0, props.limitTags).map((option: Option, index) => {
                                    return (
                                        <NeonChip
                                            key={index}
                                            label={props.getOptionShort(option)}
                                            onClick={() => unselectItem(option)}
                                            size="medium"
                                            variant="default"
                                        />
                                    );
                                })}
                                {numTags > props.limitTags && (
                                    <Typography 
                                        sx={{ 
                                            flexShrink: 0,
                                            ml: 0.5,
                                            color: 'text.secondary'
                                        }}
                                    >
                                        +{numTags - props.limitTags}
                                    </Typography>
                                )}
                            </Stack>
                        );
                    }}
                    ListboxProps={{ sx: scrollable }}
                />
                </Box>
        </Tooltip>
    );
}